import { useEffect, useState } from 'react'
import { useForm } from '@inertiajs/react'
import { Bot, Building2, UserRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import { SelectControl, TextControl } from '@/frontend/features/Fde/FormControls'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { AvailableDigitalPerson, AvailableOrganizationPerson, FdeCopy } from '@/frontend/features/Fde/types'

type Source = 'stationzero' | 'agent' | 'client'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  stationzeroPeople: AvailableOrganizationPerson[]
  agents: AvailableDigitalPerson[]
  copy: FdeCopy
  onAddClient: () => void
}

export default function PeopleAddDialog({ open, onOpenChange, projectId, stationzeroPeople, agents, copy, onAddClient }: Props) {
  const [source, setSource] = useState<Source | null>(null)
  const form = useForm({ record_id: '', role_name: '' })

  useEffect(() => {
    if (!open) return
    setSource(null)
    form.reset()
    form.clearErrors()
  }, [open])

  const options = source === 'agent'
    ? agents.map((person) => ({ value: person.id, label: person.name }))
    : stationzeroPeople.map((person) => ({ value: person.id, label: `${person.name} · ${person.email}` }))

  function selectSource(next: Source) {
    if (next === 'client') {
      onOpenChange(false)
      onAddClient()
      return
    }
    setSource(next)
    form.setData({ record_id: '', role_name: '' })
  }

  function selectRecord(id: string) {
    const selectedAgent = agents.find((agent) => agent.id === id)
    form.setData({ record_id: id, role_name: selectedAgent?.job_title || '' })
  }

  function submit() {
    if (!source) return
    const path = source === 'agent' ? fdeRoutes.projectAgents : fdeRoutes.projectMembers
    const key = source === 'agent' ? 'project_agent' : 'project_member'
    const idKey = source === 'agent' ? 'digital_worker_id' : 'user_id'
    form.transform((data) => ({ [key]: { [idKey]: data.record_id, role_name: data.role_name } }))
    form.post(fdeProjectPath(path, projectId), { preserveScroll: true, onSuccess: () => onOpenChange(false) })
  }

  return <FdeEditorDialog open={open} onOpenChange={onOpenChange} title={copy.people.add_title} closeLabel={copy.common.close} size="standard" footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>{copy.common.cancel}</Button>{source && <Button disabled={form.processing || !form.data.record_id || !form.data.role_name.trim()} onClick={submit}>{copy.common.add}</Button>}</>}>
      {!source ? <div className="grid gap-2">
        {stationzeroPeople.length > 0 && <SourceButton icon={<UserRound />} title={copy.people.source_stationzero} description={copy.people.source_stationzero_hint} onClick={() => selectSource('stationzero')} />}
        <SourceButton icon={<Bot />} title={copy.people.source_agent} description={copy.people.source_agent_hint} onClick={() => selectSource('agent')} />
        <SourceButton icon={<Building2 />} title={copy.people.source_client} description={copy.people.source_client_hint} onClick={() => selectSource('client')} />
      </div> : <div className="grid gap-4">
        <button type="button" onClick={() => setSource(null)} className="w-fit text-xs font-medium text-primary hover:underline">{copy.people.change_source}</button>
        <SelectControl label={copy.people.person} value={form.data.record_id} onChange={selectRecord} options={options} placeholder={options.length ? copy.people.select_person : copy.people.no_available_people} disabled={options.length === 0} />
        <TextControl label={copy.people.project_role} value={form.data.role_name} onChange={(event) => form.setData('role_name', event.target.value)} placeholder={copy.people.project_role_placeholder} required />
      </div>}
  </FdeEditorDialog>
}

function SourceButton({ icon, title, description, onClick }: { icon: React.ReactNode; title: string; description: string; onClick: () => void }) {
  return <button type="button" onClick={onClick} className="flex items-center gap-3 rounded-lg border border-border bg-background p-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/30">
    <span className="flex size-9 items-center justify-center rounded-md bg-muted text-muted-foreground [&>svg]:size-4">{icon}</span>
    <span className="min-w-0"><span className="block text-sm font-semibold text-foreground">{title}</span><span className="mt-0.5 block text-xs leading-4 text-muted-foreground">{description}</span></span>
  </button>
}
